/**
 * Persistent Audit Log
 * 
 * Logs all data changes to localStorage with:
 * - Full data snapshots (for recovery)
 * - Timestamps
 * - Stack traces (for debugging)
 * 
 * Persists across page refreshes.
 * Keeps last 50 entries per sprint + last 20 global events.
 */

const AUDIT_LOG_KEY = 'plet_audit_log_v2';
const SPRINT_SNAPSHOTS_KEY = 'plet_sprint_snapshots';
const MAX_GLOBAL_ENTRIES = 50;
const MAX_SPRINT_ENTRIES = 30;
const MAX_SNAPSHOTS_PER_SPRINT = 10;

/**
 * Get the current audit log from localStorage
 */
export function getAuditLog() {
  try {
    return JSON.parse(localStorage.getItem(AUDIT_LOG_KEY) || '{"global":[],"sprints":{}}');
  } catch (e) {
    return { global: [], sprints: {} };
  }
}

/**
 * Get sprint snapshots from localStorage
 */
export function getSprintSnapshots() {
  try {
    return JSON.parse(localStorage.getItem(SPRINT_SNAPSHOTS_KEY) || '{}');
  } catch (e) {
    return {};
  }
}

/**
 * Log an audit event
 * @param {string} action - The action type (e.g., 'SAVE', 'DELETE', 'SYNC')
 * @param {object} details - Details about the event
 * @param {object} sprint - Optional sprint data to snapshot
 */
export function logAuditEvent(action, details, sprint = null) {
  const timestamp = new Date().toISOString();
  const entry = {
    timestamp,
    action,
    ...details,
    // Capture where this was called from (for debugging)
    caller: new Error().stack?.split('\n')[2]?.trim() || 'unknown',
  };
  
  // Only log errors/warnings to console (keep normal audit events silent)
  if (action.includes('ERROR') || action.includes('LOSS')) {
    console.log(
      `%c[AUDIT] ${action}`,
      'background: #ff6b6b; color: white; padding: 2px 6px; border-radius: 3px;',
      details
    );
  } else if (action.includes('WARN')) {
    console.log(
      `%c[AUDIT] ${action}`,
      'background: #feca57; color: black; padding: 2px 6px; border-radius: 3px;',
      details
    );
  }
  
  try {
    const log = getAuditLog();
    
    // Add to global log
    log.global.push(entry);
    if (log.global.length > MAX_GLOBAL_ENTRIES) {
      log.global = log.global.slice(-MAX_GLOBAL_ENTRIES);
    }
    
    // Add to sprint-specific log if we have a sprint ID
    const sprintId = details.sprintId || sprint?.id;
    if (sprintId) {
      if (!log.sprints[sprintId]) {
        log.sprints[sprintId] = [];
      }
      log.sprints[sprintId].push(entry);
      if (log.sprints[sprintId].length > MAX_SPRINT_ENTRIES) {
        log.sprints[sprintId] = log.sprints[sprintId].slice(-MAX_SPRINT_ENTRIES);
      }
    }
    
    localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(log));
    
    // Save sprint snapshot if provided and has tasks
    if (sprint && sprint.id && sprint.tasks) {
      saveSprintSnapshot(sprint);
    }
  } catch (e) {
    console.error('Failed to save audit log:', e);
  }
}

/**
 * Save a snapshot of sprint data (for recovery)
 */
export function saveSprintSnapshot(sprint) {
  if (!sprint?.id || !sprint?.tasks) return;
  
  try {
    const snapshots = getSprintSnapshots();
    
    if (!snapshots[sprint.id]) {
      snapshots[sprint.id] = [];
    }
    
    // Don't save duplicate snapshots (same task count and IDs)
    const lastSnapshot = snapshots[sprint.id][snapshots[sprint.id].length - 1];
    const currentTaskIds = sprint.tasks.map(t => t.id).sort().join(',');
    const lastTaskIds = lastSnapshot?.taskIds || '';
    
    if (currentTaskIds === lastTaskIds) {
      return; // No change, don't save duplicate
    }
    
    // Save snapshot with essential data
    const snapshot = {
      timestamp: new Date().toISOString(),
      sprintId: sprint.id,
      sprintName: sprint.name,
      taskCount: sprint.tasks.length,
      taskIds: currentTaskIds,
      // Store full task data for recovery
      tasks: sprint.tasks.map(t => ({
        id: t.id,
        name: t.name,
        status: t.status,
        dependsOn: t.dependsOn,
        nestedSprintId: t.nestedSprintId,
        x: t.x,
        y: t.y,
        // Include other important fields
        category: t.category,
        notes: t.notes,
        subtasks: t.subtasks,
        deliverables: t.deliverables,
        inputs: t.inputs,
      })),
    };
    
    snapshots[sprint.id].push(snapshot);
    
    // Keep only last N snapshots per sprint
    if (snapshots[sprint.id].length > MAX_SNAPSHOTS_PER_SPRINT) {
      snapshots[sprint.id] = snapshots[sprint.id].slice(-MAX_SNAPSHOTS_PER_SPRINT);
    }
    
    localStorage.setItem(SPRINT_SNAPSHOTS_KEY, JSON.stringify(snapshots));
  } catch (e) {
    console.error('Failed to save sprint snapshot:', e);
  }
}

/**
 * Get recent snapshots for a sprint (for recovery)
 */
export function getSprintSnapshotsForRecovery(sprintId) {
  const snapshots = getSprintSnapshots();
  return snapshots[sprintId] || [];
}

/**
 * Clear audit logs (for testing/cleanup)
 */
export function clearAuditLogs() {
  localStorage.removeItem(AUDIT_LOG_KEY);
  localStorage.removeItem(SPRINT_SNAPSHOTS_KEY);
  console.log('Audit logs cleared');
}

/**
 * Export audit logs as JSON (for debugging)
 */
export function exportAuditLogs() {
  return {
    auditLog: getAuditLog(),
    snapshots: getSprintSnapshots(),
    exportedAt: new Date().toISOString(),
  };
}

/**
 * Console helper to view logs
 * Usage: In browser console, run: window.pletAudit.show()
 */
if (typeof window !== 'undefined') {
  window.pletAudit = {
    // Log an audit event (can be called from anywhere)
    log: logAuditEvent,
    show: () => {
      console.log('=== AUDIT LOG ===');
      console.table(getAuditLog().global);
      return getAuditLog();
    },
    showSprint: (sprintId) => {
      const log = getAuditLog();
      console.log(`=== AUDIT LOG FOR ${sprintId} ===`);
      console.table(log.sprints[sprintId] || []);
      return log.sprints[sprintId];
    },
    snapshots: () => {
      console.log('=== SPRINT SNAPSHOTS ===');
      const snapshots = getSprintSnapshots();
      Object.entries(snapshots).forEach(([id, snaps]) => {
        console.log(`\n${id}:`);
        console.table(snaps.map(s => ({
          time: s.timestamp,
          name: s.sprintName,
          tasks: s.taskCount,
        })));
      });
      return snapshots;
    },
    recover: (sprintId, snapshotIndex = -1) => {
      const snapshots = getSprintSnapshotsForRecovery(sprintId);
      if (snapshots.length === 0) {
        console.error('No snapshots found for', sprintId);
        return null;
      }
      const idx = snapshotIndex < 0 ? snapshots.length + snapshotIndex : snapshotIndex;
      const snapshot = snapshots[idx];
      if (!snapshot) {
        console.error('Snapshot not found at index', idx);
        return null;
      }
      console.log('Recovery snapshot:', snapshot);
      console.log('Tasks:', snapshot.tasks);
      return snapshot;
    },
    export: exportAuditLogs,
    clear: clearAuditLogs,
  };
  
}

export default {
  log: logAuditEvent,
  getLog: getAuditLog,
  getSnapshots: getSprintSnapshots,
  getSprintSnapshots: getSprintSnapshotsForRecovery,
  saveSnapshot: saveSprintSnapshot,
  export: exportAuditLogs,
  clear: clearAuditLogs,
};

